/**
 * Workforce Edit-Access Admin Page
 *
 * Site administrators (admin / super_admin / tenant_admin with site access)
 * review pending workforce edit-access requests, approve / deny pending ones,
 * and revoke previously approved grants.
 *
 * Cross-site editing is intentionally not supported — every grant is per
 * (user, site). Only requests for sites the admin can access are visible.
 *
 * SOC 2 Controls:
 * - CC6.2: Granular per-(user, site) edit privilege
 * - CC6.7: Default-deny — non-admins cannot edit until granted
 * - CC7.2: All decisions logged via backend audit_logs
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import TabNavigation from '../../components/TabNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ShieldCheck, Check, X, RotateCcw, Filter } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import { usePermissions } from '../../context/PermissionContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { key: 'pending',  label: 'Pending',  color: 'amber' },
  { key: 'approved', label: 'Approved', color: 'green' },
  { key: 'denied',   label: 'Denied',   color: 'red' },
  { key: 'revoked',  label: 'Revoked',  color: 'gray' },
];

const STATUS_BADGE = {
  pending:  'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  denied:   'bg-red-100 text-red-800',
  revoked:  'bg-gray-200 text-gray-700',
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
};

const WorkforceAccess = ({ user, onLogout, hideNavigation = false }) => {
  const { token } = useAuth();
  const { sites } = useOrg();
  const { isAdmin } = usePermissions();

  const [activeStatus, setActiveStatus] = useState('pending');
  const [siteFilter, setSiteFilter] = useState(''); // '' = all
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actingId, setActingId] = useState(null); // id of row currently being acted upon
  const [noteDraft, setNoteDraft] = useState({});

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Build a quick site_id → site_name map for display
  const siteNameById = useMemo(() => {
    const map = {};
    (sites || []).forEach(s => { map[s.id] = s.name; });
    return map;
  }, [sites]);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('status', activeStatus);
      if (siteFilter) params.append('site_id', siteFilter);
      const res = await axios.get(
        `${API_BASE}/api/workforce/access-requests?${params.toString()}`,
        { headers }
      );
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load access requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [headers, activeStatus, siteFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleAction = async (request, action) => {
    setActingId(request.id);
    try {
      const note = noteDraft[request.id] || null;
      await axios.post(
        `${API_BASE}/api/workforce/access-requests/${request.id}/${action}`,
        { note },
        { headers }
      );
      setNoteDraft(prev => { const next = { ...prev }; delete next[request.id]; return next; });
      await fetchRequests();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${action} request`);
    } finally {
      setActingId(null);
    }
  };

  if (!isAdmin()) {
    const notAllowed = (
      <div className="p-8 text-center text-gray-500">
        <p>You do not have permission to manage workforce access requests.</p>
      </div>
    );
    if (hideNavigation) return notAllowed;
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        {notAllowed}
      </div>
    );
  }

  const content = (
    <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6" />
            Workforce Edit-Access Requests
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Approve or deny per-site workforce data edit access for non-admin users. Revoke previously granted access from the Approved tab.
          </p>
        </div>

        {error && (
          <Alert className="mb-4" variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Status tabs + site filter */}
        <Card className="mb-4">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-1">
                {STATUS_TABS.map(t => (
                  <button
                    key={t.key}
                    onClick={() => setActiveStatus(t.key)}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      activeStatus === t.key
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={siteFilter}
                  onChange={(e) => setSiteFilter(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All sites</option>
                  {(sites || []).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {STATUS_TABS.find(t => t.key === activeStatus)?.label} Requests
              <span className="ml-2 text-sm font-normal text-gray-500">({requests.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center text-gray-400 py-12 text-sm">
                No {activeStatus} requests
                {siteFilter && ` for the selected site`}.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">User</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Site</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Requested</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Justification</th>
                      {activeStatus !== 'pending' && (
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Decided</th>
                      )}
                      {activeStatus !== 'pending' && (
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Note</th>
                      )}
                      <th className="text-right px-4 py-2 font-medium text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {requests.map(req => {
                      const busy = actingId === req.id;
                      return (
                        <tr key={req.id} className="hover:bg-gray-50/50">
                          <td className="px-4 py-3 align-top">
                            <div className="font-medium text-gray-800">{req.user_name || req.user_id}</div>
                            {req.user_email && (
                              <div className="text-xs text-gray-500">{req.user_email}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <span className="text-gray-700">{siteNameById[req.site_id] || req.site_id}</span>
                          </td>
                          <td className="px-4 py-3 align-top text-gray-600 whitespace-nowrap">
                            {formatDateTime(req.requested_at)}
                          </td>
                          <td className="px-4 py-3 align-top text-gray-600 max-w-xs">
                            {req.justification ? (
                              <p className="text-sm">{req.justification}</p>
                            ) : (
                              <span className="italic text-gray-400">—</span>
                            )}
                          </td>
                          {activeStatus !== 'pending' && (
                            <td className="px-4 py-3 align-top text-gray-600 whitespace-nowrap">
                              <div>{formatDateTime(req.decided_at)}</div>
                              {req.decided_by_name && (
                                <div className="text-xs text-gray-500">by {req.decided_by_name}</div>
                              )}
                            </td>
                          )}
                          {activeStatus !== 'pending' && (
                            <td className="px-4 py-3 align-top text-gray-600 max-w-xs">
                              {req.decision_note || <span className="italic text-gray-400">—</span>}
                            </td>
                          )}
                          <td className="px-4 py-3 align-top text-right">
                            {activeStatus === 'pending' && (
                              <div className="flex flex-col gap-2 items-end">
                                <input
                                  type="text"
                                  placeholder="Optional note"
                                  value={noteDraft[req.id] || ''}
                                  onChange={(e) => setNoteDraft(prev => ({ ...prev, [req.id]: e.target.value }))}
                                  className="text-xs px-2 py-1 border border-gray-300 rounded w-44 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    onClick={() => handleAction(req, 'approve')}
                                    disabled={busy}
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                  >
                                    <Check className="w-3.5 h-3.5 mr-1" /> Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => handleAction(req, 'deny')}
                                    disabled={busy}
                                    variant="outline"
                                    className="text-red-700 border-red-300 hover:bg-red-50"
                                  >
                                    <X className="w-3.5 h-3.5 mr-1" /> Deny
                                  </Button>
                                </div>
                              </div>
                            )}
                            {activeStatus === 'approved' && (
                              <div className="flex flex-col gap-2 items-end">
                                <input
                                  type="text"
                                  placeholder="Optional revoke note"
                                  value={noteDraft[req.id] || ''}
                                  onChange={(e) => setNoteDraft(prev => ({ ...prev, [req.id]: e.target.value }))}
                                  className="text-xs px-2 py-1 border border-gray-300 rounded w-44 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleAction(req, 'revoke')}
                                  disabled={busy}
                                  variant="outline"
                                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                >
                                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Revoke
                                </Button>
                              </div>
                            )}
                            {(activeStatus === 'denied' || activeStatus === 'revoked') && (
                              <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_BADGE[activeStatus]}`}>
                                {activeStatus}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
    </div>
  );

  if (hideNavigation) return content;

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      {content}
    </div>
  );
};

export default WorkforceAccess;
